import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    LayoutDashboard, 
    Users, 
    UserCog,
    Package, 
    ShoppingCart, 
    Clock, 
    DollarSign,
    Store,
    LogOut,
    Smartphone,
    Truck,
    ClipboardList,
    Wrench,
    Tag,
    Mail
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminSidebar = () => {
    const location = useLocation();
    const { user, token, logout } = useAuth();
    const [menuCounts, setMenuCounts] = useState({
        users_pending: 0,
        orders_pending: 0,
        orders_to_prepare: 0,
        sav_pending: 0
    });
    
    const isActive = (path) => location.pathname === path;

    // Fetch menu counts
    const fetchMenuCounts = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${API_URL}/admin/menu-counts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMenuCounts(response.data);
        } catch (error) {
            console.error('Error fetching menu counts:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchMenuCounts();
        // Refresh counts every 30 seconds
        const interval = setInterval(fetchMenuCounts, 30000);
        return () => clearInterval(interval);
    }, [fetchMenuCounts]);
    
    const menuItems = [
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/clients', label: 'Clients', icon: Users, countKey: 'users_pending' },
        { path: '/admin/employees', label: 'Employés', icon: UserCog },
        { path: '/admin/orders', label: 'Commandes', icon: ShoppingCart, countKey: 'orders_pending' },
        { path: '/admin/preparation', label: 'Préparation', icon: ClipboardList, countKey: 'orders_to_prepare' },
        { path: '/admin/sav', label: 'SAV', icon: Wrench, countKey: 'sav_pending' },
        { path: '/admin/communication', label: 'Communication', icon: Mail },
        { path: '/admin/products', label: 'Stock', icon: Package },
        { path: '/admin/reservations', label: 'Réservations', icon: Clock, countKey: 'reservations_active' },
        { path: '/admin/promos', label: 'Promos', icon: Tag },
        { path: '/admin/pricing', label: 'Pricing', icon: DollarSign },
        { path: '/admin/shipping', label: 'Transport', icon: Truck },
    ];

    return (
        <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-gradient-to-b from-blue-700 via-blue-800 to-blue-900 text-white flex flex-col" data-testid="admin-sidebar">
            {/* Logo */}
            <div className="p-5 border-b border-white/10">
                <Link to="/admin" className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg">
                        <Smartphone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <span 
                            className="font-bold text-lg text-white"
                            style={{ fontFamily: 'var(--font-heading)' }}
                        >
                            Mobile Hub
                        </span>
                        <span className="block text-xs text-blue-300">Administration</span>
                    </div>
                </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
                {menuItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const count = item.countKey ? menuCounts[item.countKey] : 0;
                    
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                                active 
                                    ? 'bg-white text-blue-700 shadow-lg font-semibold' 
                                    : 'text-blue-200 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
                        >
                            <div className="flex items-center gap-3">
                                <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : ''}`} />
                                <span className="font-medium">{item.label}</span>
                            </div>
                            {count > 0 && (
                                <Badge 
                                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                        active 
                                            ? 'bg-blue-600 text-white' 
                                            : 'bg-orange-500 text-white animate-pulse'
                                    }`}
                                >
                                    {count}
                                </Badge>
                            )}
                        </Link>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 space-y-3">
                {/* Switch to Client View */}
                <Link to="/catalog">
                    <Button 
                        className="w-full justify-start gap-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors"
                        data-testid="switch-to-client"
                    >
                        <Store className="w-5 h-5" />
                        Vue Client
                    </Button>
                </Link>

                {/* User info and logout */}
                <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 bg-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0 border border-blue-400/30">
                            <span className="text-sm font-bold text-white">{user?.contact_name?.charAt(0) || 'A'}</span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold truncate text-white">{user?.company_name || 'Admin'}</p>
                            <p className="text-xs text-blue-300 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={logout}
                        className="text-blue-300 hover:text-white hover:bg-white/10 rounded-lg"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </aside>
    );
};

export default AdminSidebar;
